/**
 * `/eingang/:id` — ein Beleg, ganz.
 *
 * Links die Vorschau (PDF, Bild oder die Feldtabelle einer E-Rechnung), rechts
 * die Felder und die Spur. Die Anordnung ist der Arbeitsablauf: man schaut auf
 * den Beleg und korrigiert daneben, ohne zu blaettern.
 *
 * Vier Knoepfe, und jeder hat einen Grund:
 *
 * - **Erneut auslesen** ist der Weg heraus aus „nicht lesbar", wenn die
 *   Claude-API laenger weg war (§15.2).
 * - **Erneut übergeben** legt eine zweite Zeile in `bh_datev_transfers` an —
 *   die erste bleibt, sonst faende ein Bounce nicht mehr zurueck.
 * - **Ablehnen** verlangt einen Grund.
 * - **Freigeben** gibt es nur fuer Gelb; Rot ist Admin-Sache mit Rueckruf.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, Check, RefreshCw, Send } from 'lucide-react';

import { bh, fehlerText } from '@/lib/api';
import {
  ART_LABEL,
  DATEV_STATUS_LABEL,
  STATUS_LABEL,
  STEUER_HINWEIS_LABEL,
  hatRoteWarnung,
  ibanStandAusWarnungen,
  istBearbeitbar,
} from '@/lib/beleg';
import { istAdmin } from '@/lib/rechte';
import { useAuth } from '@/features/auth/AuthContext';
import { datum, datumZeit, euro, iban as ibanFormat } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { Chip, WarnungsChip } from '@/components/ui/Chip';
import { Auswahl, Eingabe, Feld } from '@/components/ui/Feld';
import { useToast } from '@/components/ui/Toast';
import { Band, Fehler, Laden } from '@/components/ui/Zustaende';
import { AblehnenDialog, FreigebenDialog } from './BelegDialoge';
import { BelegVorschau } from './BelegVorschau';
import type { Belegart, DocumentPatch, DocumentResponse, Freigabe } from '@/types';

const ART_REIHE: Belegart[] = [
  'rechnung',
  'gutschrift',
  'mahnung',
  'bwa',
  'opos',
  'kontoauszug',
  'sonstiges',
  'unklar',
];

export function BelegPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();

  const [ablehnenOffen, setAblehnenOffen] = useState(false);
  const [freigebenOffen, setFreigebenOffen] = useState(false);

  const beleg = useQuery({
    queryKey: ['beleg', id],
    queryFn: () => bh.beleg(id),
    enabled: Boolean(id),
  });

  const log = useQuery({
    queryKey: ['beleg-log', id],
    queryFn: () => bh.belegLog(id),
    enabled: Boolean(id),
  });

  const nachAenderung = (neu: DocumentResponse) => {
    cache.setQueryData(['beleg', id], neu);
    void cache.invalidateQueries({ queryKey: ['belege'] });
    void cache.invalidateQueries({ queryKey: ['zu-zahlen'] });
    void log.refetch();
  };

  const korrigieren = useMutation({
    mutationFn: (patch: DocumentPatch) => bh.belegKorrigieren(id, patch),
    onSuccess: (neu) => {
      nachAenderung(neu);
      toast.erfolg('Gespeichert.');
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  const ablehnen = useMutation({
    mutationFn: (grund: string) => bh.belegAblehnen(id, { reason: grund }),
    onSuccess: (neu) => {
      nachAenderung(neu);
      setAblehnenOffen(false);
      toast.erfolg('Abgelehnt. Der Grund steht im Log.');
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  const freigeben = useMutation({
    mutationFn: (freigabe: Freigabe) => bh.belegFreigeben(id, freigabe),
    onSuccess: (neu, freigabe) => {
      nachAenderung(neu);
      setFreigebenOffen(false);
      toast.erfolg(
        freigabe.iban_bestaetigen
          ? 'Freigegeben. Die IBAN gilt jetzt als bestätigt — alle Admins bekommen eine Benachrichtigung.'
          : 'Freigegeben.',
      );
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  const auslesen = useMutation({
    mutationFn: () => bh.belegErneutAuslesen(id),
    onSuccess: (neu) => {
      nachAenderung(neu);
      toast.erfolg('Auslesen angestoßen.');
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  const uebergeben = useMutation({
    mutationFn: () => bh.belegErneutUebergeben(id),
    onSuccess: (neu) => {
      nachAenderung(neu);
      toast.erfolg('An die Kanzlei übergeben.');
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  const istBeleg = useMutation({
    mutationFn: (art: Belegart) => bh.belegIstBeleg(id, art),
    onSuccess: (neu) => {
      nachAenderung(neu);
      toast.erfolg('Als Beleg eingeordnet.');
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  if (beleg.isLoading) return <Laden text="Beleg wird geladen …" />;
  if (beleg.isError || !beleg.data) {
    return (
      <div className="space-y-4">
        <Zurueck />
        <Fehler text={fehlerText(beleg.error)} onErneut={() => void beleg.refetch()} />
      </div>
    );
  }

  const b = beleg.data;
  const rot = hatRoteWarnung(b.warnungen);
  const bearbeitbar = istBearbeitbar(b.status);
  const admin = istAdmin(user);
  // Nach `uebergeben` ist jede Aenderung eine Nachkorrektur mit zweiter
  // Upload-Mail „Korrektur zu <Archivname>" (§15.2). Der Knopf heisst dann
  // auch so — derselbe Endpunkt, aber ein anderer Vorgang.
  const nachkorrektur = b.status === 'uebergeben';
  const laeuft =
    korrigieren.isPending ||
    ablehnen.isPending ||
    freigeben.isPending ||
    auslesen.isPending ||
    uebergeben.isPending;

  return (
    <div>
      <Zurueck />

      <div className="mb-5 mt-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {b.supplier_name ?? b.original_name}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <Chip>{ART_LABEL[b.art]}</Chip>
            <Chip>{STATUS_LABEL[b.status]}</Chip>
            <span>
              {b.invoice_number ?? 'ohne Nummer'} · eingegangen {datumZeit(b.created_at)}
            </span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => auslesen.mutate()}
            loading={auslesen.isPending}
            disabled={laeuft}
            title="Das Auslesen von Hand anstoßen — der Weg heraus aus „nicht lesbar“."
          >
            <RefreshCw size={14} className="mr-1.5" />
            Erneut auslesen
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => uebergeben.mutate()}
            loading={uebergeben.isPending}
            disabled={laeuft || !admin}
            title={
              !admin
                ? 'Nur Admins übergeben an die Kanzlei — es geht eine Mail nach draußen.'
                : nachkorrektur
                  ? `Korrektur zu ${b.archive_name ?? 'dem übergebenen Beleg'} an die Kanzlei schicken.`
                  : 'Erneut an die Kanzlei übergeben.'
            }
          >
            <Send size={14} className="mr-1.5" />
            {nachkorrektur ? 'Korrektur an DATEV' : 'Erneut übergeben'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAblehnenOffen(true)}
            disabled={laeuft || b.status === 'abgelehnt'}
          >
            <Ban size={14} className="mr-1.5" />
            Ablehnen
          </Button>
          <Button
            size="sm"
            variant={rot ? 'danger' : 'primary'}
            onClick={() => setFreigebenOffen(true)}
            disabled={laeuft || !bearbeitbar}
            title={
              rot
                ? 'Rote Warnung: nur Admin, und nur nach Rückruf unter der bekannten Nummer.'
                : 'Freigeben und an die Kanzlei übergeben'
            }
          >
            <Check size={14} className="mr-1.5" />
            Freigeben
            {rot && (
              <span className="ml-1.5 text-xs font-normal opacity-80">
                {admin ? '(nach Rückruf)' : '(nur Admin)'}
              </span>
            )}
          </Button>
        </div>
      </div>

      {b.warnungen.length > 0 && (
        <div className="mb-4 space-y-2 rounded-xl border border-slate-200 bg-white p-4">
          {b.warnungen.map((w) => (
            <div key={w.schluessel} className="flex flex-wrap items-start gap-2">
              <WarnungsChip warnung={w} />
              <p className="flex-1 text-sm text-slate-600">{w.text}</p>
            </div>
          ))}
          {rot && (
            <p className="pt-1 text-xs text-danger-700">
              Rot heißt: erst anrufen — unter der Nummer, die ihr schon habt, nicht der aus der
              Mail. Die Freigabe mit Rückruf-Pflichtfeldern folgt.
            </p>
          )}
        </div>
      )}

      {b.art === 'sonstiges' && (
        <div className="mb-4">
          <Band>
            Als „Sonstiges" eingeordnet — nichts geht von hier nach DATEV.{' '}
            <button
              type="button"
              className="font-medium text-primary-700 hover:underline"
              onClick={() => istBeleg.mutate('rechnung')}
            >
              Ist doch ein Beleg (Rechnung)
            </button>
          </Band>
        </div>
      )}

      {b.extraction_error && (
        <div className="mb-4">
          <Band farbe="warnung">
            Auslesen fehlgeschlagen ({b.extraction_attempts}. Versuch): {b.extraction_error}
          </Band>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_400px]">
        <BelegVorschau beleg={b} />

        <div className="space-y-5">
          <Felder
            beleg={b}
            bearbeitbar={bearbeitbar}
            laeuft={korrigieren.isPending}
            onSpeichern={(patch) => korrigieren.mutate(patch)}
          />
          <Herkunft beleg={b} />
          <DatevSpur beleg={b} />
          <Zeitleiste
            eintraege={log.data ?? []}
            laedt={log.isLoading}
            fehler={log.isError ? fehlerText(log.error) : null}
          />
        </div>
      </div>

      <AblehnenDialog
        offen={ablehnenOffen}
        beleg={b}
        laeuft={ablehnen.isPending}
        onAbbrechen={() => setAblehnenOffen(false)}
        onAblehnen={(grund) => ablehnen.mutate(grund)}
      />
      <FreigebenDialog
        offen={freigebenOffen}
        beleg={b}
        laeuft={freigeben.isPending}
        onAbbrechen={() => setFreigebenOffen(false)}
        onFreigeben={(freigabe) => freigeben.mutate(freigabe)}
      />

      {/* Ein abgelehnter Beleg soll nicht offen liegen bleiben — zurueck zur
          Liste, sobald die Entscheidung durch ist. */}
      {b.status === 'abgelehnt' && b.rejected_reason && (
        <p className="mt-4 text-sm text-slate-500">
          Abgelehnt: {b.rejected_reason}.{' '}
          <button
            type="button"
            className="text-primary-700 hover:underline"
            onClick={() => navigate('/eingang')}
          >
            Zurück zum Eingang
          </button>
        </p>
      )}
    </div>
  );
}

function Zurueck() {
  return (
    <Link
      to="/eingang"
      className="inline-flex items-center gap-1.5 text-sm text-slate-500 transition-colors hover:text-slate-800"
    >
      <ArrowLeft size={14} />
      Eingang
    </Link>
  );
}

/**
 * Die Felder — editierbar bis `uebergeben`.
 *
 * Danach waere eine Aenderung eine `nachkorrektur` mit zweiter Upload-Mail an
 * die Kanzlei (§15.2). Das baut die API; hier sind die Felder so lange
 * gesperrt, mit dem Grund daneben statt eines stummen grauen Kastens.
 */
function Felder({
  beleg,
  bearbeitbar,
  laeuft,
  onSpeichern,
}: {
  beleg: DocumentResponse;
  bearbeitbar: boolean;
  laeuft: boolean;
  onSpeichern: (patch: DocumentPatch) => void;
}) {
  const [entwurf, setEntwurf] = useState<DocumentPatch>({});

  // Wechselt der Beleg (oder kommt er frisch vom Server zurueck), ist der
  // Entwurf verbraucht — sonst ueberschriebe ein alter Tippstand die Antwort.
  useEffect(() => setEntwurf({}), [beleg.id, beleg.updated_at]);

  const wert = <K extends keyof DocumentPatch>(feld: K): string =>
    String(entwurf[feld] ?? beleg[feld as keyof DocumentResponse] ?? '');

  const setzen = (feld: keyof DocumentPatch, neu: string) =>
    setEntwurf((bisher) => ({ ...bisher, [feld]: neu === '' ? null : neu }));

  const geaendert = Object.keys(entwurf).length > 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">Felder</h2>
        {geaendert && bearbeitbar && (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEntwurf({})} disabled={laeuft}>
              Verwerfen
            </Button>
            <Button size="sm" onClick={() => onSpeichern(entwurf)} loading={laeuft}>
              Speichern
            </Button>
          </div>
        )}
      </div>

      {!bearbeitbar && (
        <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
          {beleg.status === 'uebergeben'
            ? `Der Beleg ist an die Kanzlei übergeben; die Felder sind deshalb gesperrt. Eine Änderung wäre eine Nachkorrektur — sie geht als zweite Upload-Mail „Korrektur zu ${beleg.archive_name ?? 'dem Archivnamen'}“ raus, über den Knopf „Korrektur an DATEV“ oben. Nur Admin.`
            : 'Dieser Beleg ist abgeschlossen und wird nicht mehr geändert.'}
        </p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Feld label="Art">
            <Auswahl
              value={String(entwurf.art ?? beleg.art)}
              disabled={!bearbeitbar}
              onChange={(e) => setzen('art', e.target.value)}
            >
              {ART_REIHE.map((a) => (
                <option key={a} value={a}>
                  {ART_LABEL[a]}
                </option>
              ))}
            </Auswahl>
          </Feld>
        </div>

        <div className="col-span-2">
          <Feld label="Rechnungsnummer">
            <Eingabe
              value={wert('invoice_number')}
              disabled={!bearbeitbar}
              onChange={(e) => setzen('invoice_number', e.target.value)}
            />
          </Feld>
        </div>

        <Feld label="Rechnungsdatum">
          <Eingabe
            type="date"
            value={wert('invoice_date')}
            disabled={!bearbeitbar}
            onChange={(e) => setzen('invoice_date', e.target.value)}
          />
        </Feld>

        <Feld label="Fällig am">
          <Eingabe
            type="date"
            value={wert('due_date')}
            disabled={!bearbeitbar}
            onChange={(e) => setzen('due_date', e.target.value)}
          />
        </Feld>

        <Feld label="Betrag brutto" hinweis="Punkt als Trennzeichen, wie der Server ihn schickt.">
          <Eingabe
            value={wert('amount_gross')}
            disabled={!bearbeitbar}
            onChange={(e) => setzen('amount_gross', e.target.value)}
          />
        </Feld>

        <Feld label="Betrag netto">
          <Eingabe
            value={wert('amount_net')}
            disabled={!bearbeitbar}
            onChange={(e) => setzen('amount_net', e.target.value)}
          />
        </Feld>

        <Feld label="Steuersatz">
          <Eingabe
            value={wert('vat_rate')}
            disabled={!bearbeitbar}
            onChange={(e) => setzen('vat_rate', e.target.value)}
          />
        </Feld>

        <Feld label="Steuerbetrag">
          <Eingabe
            value={wert('vat_amount')}
            disabled={!bearbeitbar}
            onChange={(e) => setzen('vat_amount', e.target.value)}
          />
        </Feld>

        <div className="col-span-2">
          <Feld label="Leistung">
            <Eingabe
              value={wert('leistung')}
              disabled={!bearbeitbar}
              onChange={(e) => setzen('leistung', e.target.value)}
            />
          </Feld>
        </div>

        <div className="col-span-2">
          <Feld label="Notiz">
            <Eingabe
              value={wert('notes')}
              disabled={!bearbeitbar}
              onChange={(e) => setzen('notes', e.target.value)}
            />
          </Feld>
        </div>
      </div>

      <dl className="mt-4 space-y-1.5 border-t border-slate-100 pt-3 text-sm">
        <Zeile name="Brutto" wert={euro(beleg.amount_gross)} />
        {beleg.vat_lines && beleg.vat_lines.length > 1 && (
          <Zeile
            name="Steuersätze"
            wert={beleg.vat_lines.map((z) => `${z.satz} %`).join(' · ')}
          />
        )}
        {beleg.steuer_hinweis && (
          <Zeile name="Steuerhinweis" wert={STEUER_HINWEIS_LABEL[beleg.steuer_hinweis]} />
        )}
        <Zeile name="USt-Id" wert={beleg.aussteller_ust_id ?? '—'} />
        <Zeile
          name="Empfänger"
          wert={
            beleg.empfaenger_ok === null || beleg.empfaenger_ok === undefined
              ? '—'
              : beleg.empfaenger_ok
                ? 'Elbklar GmbH'
                : 'lautet nicht auf die Elbklar GmbH'
          }
        />
        {beleg.ist_auslage && (
          <Zeile name="Auslage von" wert={beleg.auslage_von ?? 'unbekannt'} />
        )}
        {beleg.gemahnt && (
          <Zeile name="Mahnung zu" wert={beleg.bezug_rechnungsnummer ?? 'unbekannt'} />
        )}
        {beleg.paid_at && (
          <Zeile name="Bezahlt am" wert={`${datum(beleg.paid_at)} (${euro(beleg.paid_amount)})`} />
        )}
      </dl>
    </div>
  );
}

/**
 * Woher der Beleg kam und was deterministisch aus ihm gelesen wurde.
 *
 * `ibans_im_text` ist hier der wichtigste Eintrag: `null` heisst „keine
 * Textebene, die IBAN liess sich nicht pruefen" und ist etwas anderes als eine
 * leere Liste. Genau daran haengt die gelbe Warnung `iban_nur_bild`.
 */
function Herkunft({ beleg }: { beleg: DocumentResponse }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold text-slate-900">Herkunft</h2>
      <dl className="space-y-1.5 text-sm">
        <Zeile name="Quelle" wert={beleg.source} />
        {beleg.source_mailbox && <Zeile name="Postfach" wert={beleg.source_mailbox} />}
        <Zeile name="Absender" wert={beleg.sender_address ?? '—'} />
        <Zeile
          name="Echtheit des Absenders"
          wert={
            beleg.sender_auth === 'pass'
              ? 'bestanden (SPF/DKIM)'
              : beleg.sender_auth === 'fail'
                ? 'fehlgeschlagen'
                : beleg.sender_auth
          }
        />
        <Zeile name="Rohmail" wert={beleg.hat_rohmail ? 'liegt daneben' : 'keine'} />
        <Zeile
          name="IBAN im Text"
          wert={
            beleg.ibans_im_text === null || beleg.ibans_im_text === undefined
              ? 'keine Textebene — nicht prüfbar'
              : beleg.ibans_im_text.length === 0
                ? 'Textebene da, keine IBAN gefunden'
                : beleg.ibans_im_text.map(ibanFormat).join(' · ')
          }
        />
        {/* Bestätigt oder nicht — abgeleitet aus den Warnungen, weil
            `DocumentResponse` kein Feld dafür hat (siehe
            `ibanStandAusWarnungen`). Nach einer Freigabe mit Rückruf steht
            hier „bestätigt", und daran sieht man, dass der Anruf gewirkt hat. */}
        {beleg.iban && (
          <Zeile
            name="IBAN auf dem Beleg"
            wert={`${ibanFormat(beleg.iban)} — ${
              ibanStandAusWarnungen(beleg.iban, beleg.warnungen) === 'bestaetigt'
                ? 'bestätigt'
                : 'noch nicht bestätigt'
            }`}
          />
        )}
        <Zeile name="SHA-256" wert={`${beleg.sha256.slice(0, 16)} …`} />
        {beleg.archive_name && <Zeile name="Archivname" wert={beleg.archive_name} />}
      </dl>
    </div>
  );
}

function DatevSpur({ beleg }: { beleg: DocumentResponse }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold text-slate-900">Übergabe an die Kanzlei</h2>
      {beleg.datev.length === 0 ? (
        <p className="text-sm text-slate-500">Noch nicht übergeben.</p>
      ) : (
        <ul className="space-y-3">
          {beleg.datev.map((versuch) => (
            <li key={versuch.id} className="text-sm">
              <div className="flex items-center gap-2">
                <Chip
                  klassen={
                    versuch.status === 'fehlgeschlagen'
                      ? 'border-danger-200 bg-danger-50 text-danger-700'
                      : versuch.status === 'uebergeben'
                        ? 'border-success-200 bg-success-50 text-success-700'
                        : undefined
                  }
                >
                  {DATEV_STATUS_LABEL[versuch.status]}
                </Chip>
                <span className="text-slate-400">{versuch.attempt}. Versuch</span>
              </div>
              <p className="mt-1 text-slate-600">
                an {versuch.recipient} · {datumZeit(versuch.sent_at)}
              </p>
              {versuch.error && <p className="mt-0.5 text-danger-700">{versuch.error}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Die GoBD-Spur. Append-only — was hier steht, bleibt stehen. */
function Zeitleiste({
  eintraege,
  laedt,
  fehler,
}: {
  eintraege: { id: string; step: string; actor: string; detail?: unknown; created_at: string }[];
  laedt: boolean;
  fehler: string | null;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold text-slate-900">Log</h2>
      {laedt && <p className="text-sm text-slate-500">wird geladen …</p>}
      {fehler && <p className="text-sm text-slate-500">{fehler}</p>}
      {!laedt && !fehler && eintraege.length === 0 && (
        <p className="text-sm text-slate-500">Noch keine Zeile.</p>
      )}
      <ol className="space-y-3">
        {eintraege.map((eintrag) => (
          <li key={eintrag.id} className="relative pl-5 text-sm">
            <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-slate-300" />
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium text-slate-800">{eintrag.step}</span>
              <span className="text-xs text-slate-400">
                {datumZeit(eintrag.created_at)} · {eintrag.actor}
              </span>
            </div>
            {eintrag.detail != null && (
              <pre className="mt-1 overflow-x-auto rounded bg-slate-50 px-2 py-1 text-xs text-slate-500">
                {JSON.stringify(eintrag.detail)}
              </pre>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function Zeile({ name, wert }: { name: string; wert: string }) {
  return (
    <div className="flex gap-3">
      <dt className="w-40 shrink-0 text-slate-500">{name}</dt>
      <dd className="break-words text-slate-900">{wert}</dd>
    </div>
  );
}
